import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { routeNames } from './app-routing-names';
import { AuthenticationGuard } from './guards/authentication-guard';
import { InternalAuthenticationGuard } from './guards/internal.authentication-guard';
import { AnimatedBackgroundTypes } from './models/enums/animated-background-types';
import { LayoutTypes } from './models/enums/layout-types';
import { QuoteSteps } from './models/enums/quote-steps';
import { QuoteStepGuard } from './guards/quote-step-guard';
import { VerifyQuickQuestionsGuard } from './guards/verify-quick-questions-guard';
import { ManualCarGuard } from './guards/manual-car-guard';
import { QuoteDetailsGuard } from './guards/quote-details-guard';

const routes: Routes = [
    {
        path: routeNames.LOGIN,
        loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
        data: {
            title: 'Login',
            step: QuoteSteps.none,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.QUOTE_RETRIEVAL,
        canActivate: [AuthenticationGuard],
        loadComponent: () => import('./pages/quote-retrieval/quote-retrieval.component').then(m => m.QuoteRetrievalComponent),
        data: {
            title: 'Quote retrieval',
            step: QuoteSteps.none, section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletTopForm
        }
    },
    {
        path: routeNames.QUOTE_SEARCH,
        canActivate: [InternalAuthenticationGuard],
        loadComponent: () => import('./pages/quote-search/quote-search.component').then(m => m.QuoteSearchComponent),
        data: {
            title: 'Quote search',
            step: QuoteSteps.none, section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.VERIFY_STARTING_INFORMATION,
        loadComponent: () => import('./pages/xsell-address-verification/xsell-address-verification.component').then(m => m.XsellAddressVerificationComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard, VerifyQuickQuestionsGuard],
        data: {
            title: 'Update Address Proposer',
            step: QuoteSteps.verifyStartingInformation,
            section: 1,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.identityInfo,
            timer: false,
            layout: LayoutTypes.routerOutletTop
        }
    },
    {
        path: routeNames.MANAGE_DISCOUNT_LEADS,
        canActivate: [AuthenticationGuard],
        loadComponent: () => import('./pages/xsell-manage-discount-leads/xsell-manage-discount-leads.component').then(m => m.XsellManageDiscountLeadsComponent),
        data: {
            title: 'Manage Discount Leads',
            step: QuoteSteps.manageDiscountLeads,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.documentPositive,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.OPT_OUT_DISCOUNT_LEADS,
        canActivate: [AuthenticationGuard],
        loadComponent: () => import('./pages/xsell-opt-out-discount-leads/xsell-opt-out-discount-leads.component').then(m => m.XsellOptOutDiscountLeadsComponent),
        data: {
            title: 'Opt Out Discount Leads',
            step: QuoteSteps.optOutDiscountLeads,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.documentPositive,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.PROPOSER_XSELL_QUOTE,
        canActivate: [AuthenticationGuard, QuoteStepGuard, VerifyQuickQuestionsGuard],
        loadComponent: () => import('./pages/proposer-xsell-quote/proposer-xsell-quote.component').then(m => m.ProposerXsellQuoteComponent),
        data: {
            title: 'Proposer Xsell Quote',
            step: QuoteSteps.xsellQuote,
            section: 1,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletCenter
        }
    },
    {
        path: routeNames.QUICK_QUESTIONS_SECTION_ONE,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/confirmation/quick-questions/quick-question-section-one/quick-question-section-one.component').then(m => m.QuickQuestionSectionOneComponent),
        data: {
            title: 'page-one',
            step: QuoteSteps.quickQuestionSectionOne,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletCenter
        }
    },
    {
        path: routeNames.QUICK_QUESTIONS_SECTION_TWO,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/confirmation/quick-questions/quick-question-section-two/quick-question-section-two.component').then(m => m.QuickQuestionSectionTwoComponent),
        data: {
            title: 'page-two',
            step: QuoteSteps.quickQuestionSectionTwo,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletCenter
        }
    },
    {
        path: routeNames.QUICK_QUESTIONS_SECTION_THREE,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/confirmation/quick-questions/quick-question-section-three/quick-question-section-three.component').then(m => m.QuickQuestionSectionThreeComponent),
        data: {
            title: 'page-three',
            step: QuoteSteps.quickQuestionSectionOne,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletCenter
        }
    },
    {
        path: routeNames.QUICK_QUESTIONS_CONFIRMATION,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/confirmation/quick-questions/quick-question-confirmation/quick-question-confirmation.component').then(m => m.QuickQuestionConfirmationComponent),
        data: {
            title: 'confirmation',
            step: QuoteSteps.quickQuestionConfirmation,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletCenter
        }
    },
    {
        path: routeNames.SELECT_PRODUCT,
        loadComponent: () => import('./pages/select-product/select-product.component').then(m => m.SelectProductComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard, VerifyQuickQuestionsGuard],
        data: {
            title: 'Select Product',
            step: QuoteSteps.selectProduct,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletCenterFull
        }
    },
    {
        path: routeNames.SEARCH_REG,
        loadComponent: () => import('./pages/about-your-car/search-reg/search-reg.component').then(m => m.SearchRegComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard, VerifyQuickQuestionsGuard],
        data: {
            title: 'Search Registration',
            step: QuoteSteps.searchReg,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.minivan,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.MANUAL_CAR_LOOKUP,
        loadComponent: () => import('./pages/about-your-car/manual-car-lookup/manual-car-lookup.component').then(m => m.ManualCarLookupComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard, ManualCarGuard],
        data: {
            title: 'Manual Car Lookup',
            step: QuoteSteps.manualCarLookup,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.minivan,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.PURCHASE_DATE_AND_VALUE,
        loadComponent: () => import('./pages/about-your-car/purchase-date/purchase-date-and-value.component').then(m => m.PurchaseDateAndValueComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Purchase date',
            step: QuoteSteps.purchaseDateAndValue,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.smallCar,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.USE_AND_MILEAGE,
        loadComponent: () => import('./pages/about-your-car/use/use.component').then(m => m.UseComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Use',
            step: QuoteSteps.useAndMileage,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.locationScene,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.MILEAGE_CALCULATOR,
        loadComponent: () => import('./pages/about-your-car/mileage-calculator/mileage-calculator.component').then(m => m.MileageCalculatorComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Mileage',
            step: QuoteSteps.mileageCalculator,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.locationScene,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.COVER_START,
        loadComponent: () => import('./pages/about-your-car/cover-start/cover-start.component').then(m => m.CoverStartComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Cover Start',
            step: QuoteSteps.coverStart,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.calendar,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.POLICY_INFO,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/quote/policy-info/policy-info.component').then(m => m.PolicyInfoComponent),
        data: {
            title: 'Policy Info',
            step: QuoteSteps.policyInfo,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.redCar,
            timer: true,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.LICENCE_TYPE_AND_AGE,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/your-driving-record/driving-history/licence-type/licence-type.component').then(m => m.LicenceTypeComponent),
        data: {
            title: 'Licence type',
            step: QuoteSteps.licenceTypeAndAge,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.identityCard,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.PENALTY_POINTS,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/your-driving-record/penalty-points/has-penalty-points.component').then(m => m.HasPenaltyPointsComponent),
        data: {
            title: 'Penalty points',
            step: QuoteSteps.penaltyPoints,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.steeringWheelNegative,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.CONVICTIONS,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/your-driving-record/convictions/convictions.component').then(m => m.ConvictionsComponent),
        data: {
            title: 'Convictions',
            step: QuoteSteps.convictions,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.identityInfo,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.MEDICAL_CONDITION,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/your-driving-record/medical-condition/medical-condition.component').then(m => m.MedicalConditionComponent),
        data: {
            title: 'Medical condition',
            step: QuoteSteps.medicalCondition,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.medicalEquipment,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.YEARS_OF_COVER,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/your-driving-record/driving-history/years-of-cover/years-of-cover.component').then(m => m.YearsOfCoverComponent),
        data: {
            title: 'Years of cover',
            step: QuoteSteps.yearsOfCover,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.greenCar,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.NAMED_DRIVER_EXPERIENCE,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/your-driving-record/driving-history/named-driver-experience/named-driver-experience.component').then(m => m.NamedDriverExperienceComponent),
        data: {
            title: 'Named Driver Experience',
            step: QuoteSteps.namedDriverExperience,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.yellowCar,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.CLAIMS,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/your-driving-record/has-claims/has-claims.component').then(m => m.HasClaimsComponent),
        data: {
            title: 'Claims',
            step: QuoteSteps.claims,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.clipboardMotor,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.HAS_OTHER_POLICIES,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/about-you/has-other-policies/has-other-policies.component').then(m => m.HasOtherPoliciesComponent),
        data: {
            title: 'Has other policies',
            step: QuoteSteps.hasOtherPolicies,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.discount,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.EMPLOYMENT_STATUS,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/about-you/employment-status/employment-status.component').then(m => m.EmploymentStatusComponent),
        data: {
            title: 'Employment status',
            step: QuoteSteps.employmentStatus,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.identityInfo,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.OCCUPATION,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/about-you/occupation/occupation.component').then(m => m.OccupationComponent),
        data: {
            title: 'Occupation',
            step: QuoteSteps.occupation,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.jobs,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        },
    },
    {
        path: routeNames.SEARCH_EIRCODE,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/about-you/search-eircode/search-eircode.component').then(m => m.SearchEircodeComponent),
        data: {
            title: 'Search Eircode',
            step: QuoteSteps.searchEircode,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.locationScene,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.REGION_TOWN,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/about-you/region-town/region-town.component').then(m => m.RegionTownComponent),
        data: {
            title: 'Region town',
            step: QuoteSteps.regionTown,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.locationScene,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.CONTACT_INFO,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/about-you/contact-info/contact-Info.component').then(m => m.ContactInfoComponent),
        data: {
            title: 'Contact info',
            step: QuoteSteps.contactInfo,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.contact,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_DRIVER,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/add-driver-confirmation/add-driver-confirmation.component').then(m => m.AddDriverConfirmationComponent),
        data: {
            title: 'Additional Driver',
            step: QuoteSteps.ad_Driver,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.spouseInsurance,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_NAME,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/ad-name/ad-name.component').then(m => m.AdNameComponent),
        data: {
            title: 'Additional driver name',
            step: QuoteSteps.aD_Name,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_EMPLOYMENT_STATUS,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/ad-employment-status/ad-employment-status.component').then(m => m.AdEmploymentStatusComponent),
        data: {
            title: 'Additional drivers employment status',
            step: QuoteSteps.aD_EmploymentStatus,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_OCCUPATION,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/ad-occupation/ad-occupation.component').then(m => m.AdOccupationComponent),
        data: {
            title: 'Additional drivers occupation',
            step: QuoteSteps.aD_Occupation,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_RELATIONSHIP,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/ad-relationship/ad-relationship.component').then(m => m.AdRelationshipComponent),
        data: {
            title: 'additional driver relationship',
            step: QuoteSteps.aD_Relationship,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_HAS_OWN_CAR,
        loadComponent: () => import('./pages/additional-drivers/ad-own-car/ad-own-car.component').then(m => m.AdOwnCarComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Additional driver has own car',
            step: QuoteSteps.aD_HasOwnCar,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_RENEWAL,
        loadComponent: () => import('./pages/additional-drivers/ad-car-renewal/ad-car-renewal.component').then(m => m.AdCarRenewalComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Additional driver renewal',
            step: QuoteSteps.aD_RenewaL,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_LICENCE_TYPE,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/ad-licence-type/ad-licence-type.component').then(m => m.AdLicenceTypeComponent),
        data: {
            title: 'Additional drivers licence type',
            step: QuoteSteps.aD_licenceType,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_PENALTY_POINTS,
        loadComponent: () => import('./pages/additional-drivers/ad-penalty-points/ad-penalty-points.component').then(m => m.AdPenaltyPointsComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Additional driver has penalty points',
            step: QuoteSteps.aD_PenaltyPoints,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_CLAIMS,
        loadComponent: () => import('./pages/additional-drivers/ad-has-claims/ad-has-claims.component').then(m => m.AdHasClaimsComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'Additional driver has claims',
            step: QuoteSteps.aD_Claims,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_CONVICTIONS,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/ad-convictions/ad-convictions.component').then(m => m.AdConvictionsComponent),
        data: {
            title: 'Additional driver Convictions',
            step: QuoteSteps.aD_Convictions,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.AD_MEDICAL_CONDITION,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/additional-drivers/ad-medical-condition/ad-medical-condition.component').then(m => m.AdMedicalConditionComponent),
        data: {
            title: 'Additional driver Medical conditional',
            step: QuoteSteps.aD_MedicalCondition, canBackNav: false,
            section: 4,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.GET_QUOTE,
        canActivate: [AuthenticationGuard, QuoteStepGuard, QuoteDetailsGuard],
        loadComponent: () => import('./pages/quote/get-quote/get-quote.component').then(m => m.GetQuoteComponent),
        data: {
            title: 'Get quote',
            step: QuoteSteps.getQuote,
            section: 3,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.break_screen_3,
            timer: false,
            layout: LayoutTypes.routerOutletNone,
            checkOnEditFlag: false
        }
    },
    {
        path: routeNames.QUOTE_DETAILS,
        canActivate: [AuthenticationGuard, QuoteStepGuard, QuoteDetailsGuard],
        loadChildren: () => import(`./pages/quote/quote-details/quote-details.module`).then(m => m.QuoteDetailsModule),
    },
    {
        path: routeNames.CICA,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/quote/cica/cica.component').then(m => m.CicaComponent),
        data: {
            title: 'CICA',
            step: QuoteSteps.cica,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.clipboardMotor,
            timer: true,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.CICA_SELECTION,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/quote/cica-selection/cica-selection.component').then(m => m.CicaSelectionComponent),
        data: {
            title: 'CICA selection',
            step: QuoteSteps.cicaSelection,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.clipboardPencil,
            timer: true,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.BLACK_BOX_DETAILS,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/quote/black-box-details/black-box-details.component').then(m => m.BlackBoxDetailsComponent),
        data: {
            title: 'Telematics Black Box',
            step: QuoteSteps.blackBoxDetails,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: true,
            layout: LayoutTypes.routerOutletCenterFull
        }
    },
    {
        path: routeNames.DRIVER_NUMBER,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadChildren: () => import(`./pages/quote/mtpl-driver-number/mtpl-driver-number.module`).then(m => m.MtplDriverNumberModule),
        data: {
            title: 'Driver numbers',
            step: QuoteSteps.driverNumber,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: true,
            layout: LayoutTypes.routerOutletMidFull
        }
    },
    {
        path: routeNames.CHOOSE_PAYMENT,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/choose-payment/choose-payment.component').then(m => m.ChoosePaymentComponent),
        data: {
            title: 'Choose payment',
            step: QuoteSteps.choosePayment,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: true,
            layout: LayoutTypes.routerOutletEnd
        }
    },
    {
        path: routeNames.PAYMENT_CC,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/payment-cc/payment-cc.component').then(m => m.PaymentCcComponent),
        data: {
            title: 'Payment',
            step: QuoteSteps.paymentCC,
            canBackNav: false,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: true,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.PAYMENT_DD,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/payment-dd/payment-dd.component').then(m => m.PaymentDdComponent),
        data: {
            title: 'Payment',
            step: QuoteSteps.paymentDD,
            canBackNav: false,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: true,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.PROCESSING_PAYMENT,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/payment-cc/processing-payment/processing-payment.component').then(m => m.ProcessingPaymentComponent),
        data: {
            title: 'Processing Payment',
            step: QuoteSteps.processingPayment,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: true,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.CONFIRMATION,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/confirmation/confirmation.component').then(m => m.ConfirmationComponent),
        data: {
            title: 'Confirmation',
            step: QuoteSteps.confirmation,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.ABOUT_YOUR_CAR_BREAK,
        loadComponent: () => import('./pages/about-your-car-break/about-your-car.component').then(m => m.AboutYourCarBreakComponent),
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        data: {
            title: 'About your Car Break',
            step: QuoteSteps.aboutYourCarBreak,
            section: 1,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_2,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.DRIVING_RECORD_BREAK,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/driving-record-break/driving-record-break.component').then(m => m.DrivingRecordBreakComponent),
        data: {
            title: 'Driving Record Break',
            step: QuoteSteps.drivingRecordBreak,
            section: 2,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.break_screen_1,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.ABOUT_YOU,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/about-you/policy-holder-info/policy-holder-info.component').then(m => m.PolicyHolderInfoComponent),
        data: {
            title: 'About you',
            step: QuoteSteps.aboutYou,
            section: 3,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.identityInfo,
            timer: false,
            layout: LayoutTypes.routerOutletNone
        }
    },
    {
        path: routeNames.NO_QUOTE,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/error/no-quote/no-quote.component').then(m => m.NoQuoteComponent),
        data: {
            title: 'No quote',
            step: QuoteSteps.noQuote,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.XSELL_ERROR,
        loadComponent: () => import('./pages/error/application-error/application-error.component').then(m => m.ApplicationErrorComponent),
        data: {
            title: 'XSell Error',
            step: QuoteSteps.xsellError,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.APPLICATION_ERROR,
        loadComponent: () => import('./pages/error/application-error/application-error.component').then(m => m.ApplicationErrorComponent),
        data: {
            title: 'Error',
            step: QuoteSteps.applicationError,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.DUP_PAYMENT_ERROR,
        loadComponent: () => import('./pages/error/application-error/application-error.component').then(m => m.ApplicationErrorComponent),
        data: {
            title: 'Error',
            step: QuoteSteps.dupPaymentError,
            section: 5,
            backButton: true,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },

    {
        path: routeNames.ACCEPTANCE_NOT_MET,
        canActivate: [AuthenticationGuard, QuoteStepGuard],
        loadComponent: () => import('./pages/error/no-quote/no-quote.component').then(m => m.NoQuoteComponent),
        data: {
            title: 'Acceptance not met',
            step: QuoteSteps.acceptanceNotMet,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    {
        path: routeNames.CAPTCHA_ERROR,
        loadComponent: () => import('./pages/error/captcha-error/captcha-error.component').then(m => m.CaptchaErrorComponent),
        data: {
            title: 'Bot security',
            step: QuoteSteps.captchaError,
            section: 5,
            backButton: false,
            backgroundImage: AnimatedBackgroundTypes.none,
            timer: false,
            layout: LayoutTypes.routerOutletFull
        }
    },
    { path: '**', redirectTo: routeNames.SELECT_PRODUCT, pathMatch: 'full' }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'disabled' })],
    exports: [RouterModule]
})
export class AppRoutingModule { }